import { Entitlements } from '../models/entitlements';

/** The entitlement flags a How To can be gated on — the boolean ones only. */
export type BooleanEntitlement = {
  [K in keyof Entitlements]: Entitlements[K] extends boolean ? K : never;
}[keyof Entitlements];

/**
 * In-app help — content types.
 *
 * How Tos live in code, not the database: their steps point at real elements
 * via `data-help` anchors, so they have to version with the UI. See
 * docs/Help-System-POC-Plan.md.
 */

/** Sections, in left-menu order — search results group under these. */
export const HELP_SECTIONS = [
  'Dashboard',
  'Posts',
  'Weekly Schedule',
  'Attendance',
  'Attendance Stats',
  'Insights',
  'Reports',
  'Documents',
  'Admin',
  // Not a page: the PIN, the active user, the device sign-in. Sections do not
  // have to correspond to a route — this one has no entry in SECTION_ROUTES,
  // so its group heading carries no jump link.
  'Security',
  // Not a page either: getting help from Rustle It when no How To answers it.
  // Last, because it is the way out of the help rather than part of it.
  'Support',
] as const;

export type HelpSection = typeof HELP_SECTIONS[number];

/**
 * Where a section lives, for the "go to this page" link on a group heading.
 * Security has no page of its own (the PIN, the profile menu and the password
 * live in menus and popups), so it has no link — and a section is free not to.
 */
export const SECTION_ROUTES: Partial<Record<HelpSection, string>> = {
  'Dashboard': '/',
  'Posts': '/posts',
  'Weekly Schedule': '/schedule',
  'Attendance': '/attendance',
  'Attendance Stats': '/attendance-stats',
  'Insights': '/insights',
  'Reports': '/reports',
  'Documents': '/documents',
  'Admin': '/admin',
};

/** A detour a step can offer: another How To, needed at that exact moment. */
export interface HowToSeeAlso {
  guideKey: string;
  /** The offer, in the user's words. Defaults to the other How To's title. */
  label?: string;
}

export interface HowToStep {
  /** One or two sentences. `**bold**` marks UI labels. */
  text: string;
  /**
   * A `data-help` anchor to highlight, e.g. 'schedule.grid-lock'. Absent = no
   * ring; the step is still shown.
   *
   * A LIST means "whichever of these is on screen", most specific first — so
   * "open Admin, then the Report Templates tab" can ring the menu item from
   * elsewhere and the tab strip once you are there, moving as you arrive,
   * without costing a step that half the readers have already done.
   */
  target?: string | string[];
  /** The page this step happens on. Off it, the window offers "Take me there". */
  route?: string;
  /**
   * The step's whole job is getting to `route` — "Open Attendance from the
   * menu", "…then the Users tab". "Take me there" then completes it, so the
   * guide moves on once the move has happened.
   *
   * Leave it off when the step asks for anything once there (pick a child,
   * move to a week, open a post) or points at something on the page: the
   * reader has to arrive and still see the step, or "Take me there" skips the
   * very thing they were taken there to do. Explicit rather than inferred —
   * "Open Weekly Schedule from the menu and move to the week…" has the same
   * route and the same menu target as a step that only opens it.
   */
  navigateOnly?: boolean;
  /**
   * Another How To this step may send the user to — used ONLY where the step
   * names an action they might need right now (blurring a face at the photo
   * step; clearing attendance at the step that is blocked by it), never as a
   * substitute for saying what to do.
   *
   * Following it remembers where they were and hands them back afterwards. One
   * level deep: a detour from a detour replaces the memory rather than nesting,
   * which keeps this a breadcrumb rather than the guide stack we deferred.
   */
  seeAlso?: HowToSeeAlso;
}

export interface HowTo {
  /** Stable id, '<section>.<task>'. Used by persistence — renaming resets progress. */
  key: string;
  /** Bumped when the steps change materially; a stored guide on an old
   *  version restarts rather than resuming mid-way through different steps. */
  version: number;
  section: HelpSection;
  /** Task-phrased: "Change the time of a child's session this week". */
  title: string;
  summary: string;
  /** Plain-text requirements shown in the preview. Never evaluated — the app
   *  itself enforces its rules (see the plan, Part 3). */
  youNeed?: string[];
  /** The user's words, not ours: 'wrong time', 'longer', 'move'. */
  keywords: string[];
  /**
   * Pathnames this guide is listed on. The browse list groups by section and
   * lifts the current page's section to the top, so these decide where a guide
   * shows up rather than producing a list of their own.
   *
   * **Empty means unlisted** — never browsed into, still found by search and
   * still openable from a `HelpAsk` in the app. For a guide whose relevance
   * only the app can judge (Set up your PIN, which turns on `hasPin`), that is
   * the difference between help at the moment of need and an offer to do
   * something the reader did months ago.
   */
  routes: string[];
  audience: 'staff' | 'external' | 'admin' | 'all';
  /**
   * Followable before anyone has signed in — i.e. at the profile picker, where
   * there is no active user, no menu and no app to navigate into.
   *
   * It is a property of the STEPS, not of the subject: a guide qualifies only
   * if every step happens on a pre-sign-in screen, points at something on it,
   * and asks the reader to do nothing that needs an account. A guide about
   * PINs that tells you to click your avatar does not qualify, however
   * relevant its title looks on that screen.
   */
  availableSignedOut?: boolean;
  /** Role name as it appears in the roles map, e.g. 'Reporting'. */
  requiredRole?: string;
  /** A boolean the plan has to include, e.g. 'aiFullEnabled'. Guides for a
   *  surface the organisation hasn't bought shouldn't be listed at all —
   *  same reasoning as the nav, which omits Insights rather than teasing it. */
  entitlement?: BooleanEntitlement;
  steps: HowToStep[];
  /** Keys of related How Tos, offered at the end. */
  related?: string[];
}
